/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/naming-convention */
import { Injectable } from '@angular/core';
import {
	CheckOutRecordDto,
	ExternalTransferApiService,
	TransferRecordDto,
	TransferRecordIdentityBuilder
} from '@api/external-transfer';
import { ApiConnectionsModel } from '@database/central';
import { FileExporterDataProvider } from '../../../json-file-exporter/file-exporter.service';
import { ErrorCodes, StorageService } from '@shared';
import { first, forkJoin, map, Observable, switchMap, throwError, from } from 'rxjs';
import { BusinessLayerService } from '../../services/business-layer.service';
import { CopyinCheckinCheckoutModel } from '../models/copyin-checkin-checkout.model';
import { ApiActionTypeEnum } from '../models/api-action-type.enum';
import { DeviceIdProvider } from '@database/workspace';
import jwt_decode from 'jwt-decode';
import { DecodeJWTResultModel } from '@domain/registration';

@Injectable()
export class CopyinCheckinCheckoutService {

	public currentDeviceId: string;
	public tokenDeviceId: any;
	public workfileDeviceId: string;

	public constructor(
		private bll: BusinessLayerService,
		private externalTransferApiService: ExternalTransferApiService,
		private fileExporterDataProvider: FileExporterDataProvider,
		private deviceIdProvider: DeviceIdProvider,
		private storageService: StorageService
	) { }

	public copyIn$(entityModel: CopyinCheckinCheckoutModel): Observable<any> {
		return from(this.getDatabaseUsingWorkFileId(entityModel.workfileId)).pipe(
			switchMap((database: string) =>
				this.bll.getConnectionModel$(entityModel.workfileId).pipe(
					switchMap((apiConnectionModel: ApiConnectionsModel) => {
						if (!apiConnectionModel) {
							throwError(() => ErrorCodes.NoConnectionAssociated);
						}
						return forkJoin({
							transferRecord: this.getTransferRecordIdentity(
								entityModel.workfileId,
								entityModel.projectNumber,
								entityModel.entityName,
								entityModel.id,
								database ? database : apiConnectionModel.selectedOptions.database
							),
							workfile: this.fileExporterDataProvider.exportCopyInCheckIn$(
								entityModel.workfileId,
								entityModel.entityName,
								entityModel.id,
								ApiActionTypeEnum.COPYIN
							)
						}).pipe(
							switchMap((result) => {
								const transferId = result.transferRecord;
		
								return from(this.checkDeviceId(JSON.stringify(result.workfile))).pipe(
									switchMap((deviceIdResult) => {
										console.log(deviceIdResult, "copy copyinCheckinCheckoutService 2", transferId,);
										transferId.Workfile = deviceIdResult;
										transferId.RecordId = `${entityModel.entityName}-${entityModel.id}`;
										return this.externalTransferApiService.copyIn(apiConnectionModel.connectionId, transferId);
									})
								);
							})
						);
					})
				)
			)
		);
	}

	public checkIn$(entityModel: CopyinCheckinCheckoutModel): Observable<any> {
		return from(this.getDatabaseUsingWorkFileId(entityModel.workfileId)).pipe(
			switchMap((database: string) =>
				this.bll.getConnectionModel$(entityModel.workfileId).pipe(
					switchMap((apiConnectionModel: ApiConnectionsModel) => {
						if (!apiConnectionModel) {
							return throwError(() => ErrorCodes.NoConnectionAssociated);
						}

						return forkJoin({
							transferRecord: this.getTransferRecordIdentity(
								entityModel.workfileId,
								entityModel.projectNumber,
								entityModel.entityName,
								entityModel.id,
								database ? database : apiConnectionModel.selectedOptions.database
							),
							workfile: this.fileExporterDataProvider.exportCopyInCheckIn$(
								entityModel.workfileId,
								entityModel.entityName,
								entityModel.id,
								ApiActionTypeEnum.CHECKIN
							)
						}).pipe(
							switchMap((result) => {
								console.log(result, "checkin result copyinCheckinCheckoutService");
								const transferId = result.transferRecord;

								return from(this.checkDeviceId(JSON.stringify(result.workfile))).pipe(
									switchMap((deviceIdResult) => {
										console.log(deviceIdResult, "checkin copyinCheckinCheckoutService 2", transferId,);
										transferId.RecordId = `${entityModel.entityName}-${entityModel.id}`;
										transferId.Workfile = deviceIdResult;
										return this.externalTransferApiService.checkIn(apiConnectionModel.connectionId, transferId);
									})
								);
							})
						);
					})
				)
			)
		);
	}

	public async getDatabaseUsingWorkFileId(workFileId: string): Promise<string> {
		return await this.storageService.get(workFileId + '_database');
	}

	public checkOut$(entityModel: CopyinCheckinCheckoutModel): Observable<any> {
		return from(this.getDatabaseUsingWorkFileId(entityModel.workfileId)).pipe(
			switchMap((database: string) =>
				this.bll.getConnectionModel$(entityModel.workfileId).pipe(
					switchMap((apiConnectionModel: ApiConnectionsModel) => {
						if (!apiConnectionModel) {
							throwError(() => ErrorCodes.NoConnectionAssociated);
						}
						return forkJoin({
							transferRecord: this.getTransferRecordIdentity(
								entityModel.workfileId,
								entityModel.projectNumber,
								entityModel.entityName,
								entityModel.id,
								database ? database : apiConnectionModel.selectedOptions.database
							),
							workfile: this.fileExporterDataProvider.exportCheckOut$()
						}).pipe(
							switchMap((result) => {
								const checkOutRecord = new CheckOutRecordDto({
									TransferRecord: result.transferRecord.TransferRecord,
									AuthenticationMethod: result.transferRecord.AuthenticationMethod,
									Environment: result.transferRecord.Environment,
									LoggingStyle: apiConnectionModel.selectedOptions.dhlLoggingStyle,
									SstnLoggingStyle: apiConnectionModel.selectedOptions.sstnLoggingStyle,
									BusinessUnit: apiConnectionModel.selectedOptions.businessUnit,
									Workfile: JSON.stringify(result.workfile),
									RecordId: `${entityModel.entityName}-${entityModel.id}`
								});
		
								return this.externalTransferApiService.checkOut(
									apiConnectionModel.connectionId,
									checkOutRecord
								);
							})
						);
					})
				)
			)
		);
	}

	private getTransferRecordIdentity(
		workfileId: string,
		projectNumber: string,
		entityName: string,
		id: number,
		environment: string
	): Observable<TransferRecordDto> {
		return this.bll.getPrimaryKeyAndValue$(workfileId, projectNumber, entityName, id).pipe(
			map((primaryKeys) => {
				const identityBuilder = new TransferRecordIdentityBuilder().setTableName(entityName);
				primaryKeys.forEach((pk) =>
					identityBuilder.addPrimaryKey({
						ColumnName: pk.primaryKey,
						ColumnValue: pk.primaryKeyValue
					})
				);

				const transferRecordDto = new TransferRecordDto({
					TransferRecord: [identityBuilder.build()],
					Environment: environment,
					Workfile: workfileId,
					// TODO: Remove when not required anymore
					AuthenticationMethod: 'Database Authentication',
					RecordId: `${entityName}-${id}`
				});

				return transferRecordDto;
			}),
			first()
		);
	}

	public async checkDeviceId(workfile: string): Promise<string> {
		this.currentDeviceId = await this.deviceIdProvider.getDeviceId();
		const parsedWorkfile = JSON.parse(workfile);
		parsedWorkfile.FusionConfigHeader[0].device_id = this.currentDeviceId;

		const modifiedWorkfile = JSON.stringify(parsedWorkfile);
		return modifiedWorkfile
	}
}
